import type { CommentShape, CommentThreadNode, CommentThreadNodeMap } from './types.ts';

export function buildNodes<TComment extends CommentShape>(
  rootNode: CommentThreadNode<TComment>,
  comments: TComment[],
  options?: { pinnedId?: string; prepend?: boolean }
): CommentThreadNode<TComment> {
  if (!comments.length) {
    return rootNode;
  }

  // Create a deep clone of all nodes
  const result = structuredClone(rootNode);

  // Build a map of all nodes
  const nodeMap = buildNodeMap(result);

  // Build a map of pending comments without a parent
  const pendingComments = new Map<string, TComment[]>();

  comments.map(comment => {
    const existingNode = nodeMap.get(comment.id);

    // Update comment data if node exists
    if (existingNode) {
      existingNode.data = { ...comment };
    }

    // Get parent node
    const parentNode = existingNode
      ? nodeMap.get(existingNode.parentId)
      : nodeMap.get(comment.parentCommentId);

    if (!parentNode) {
      // Save comment for later if parent node does not exist yet
      if (comment.parentCommentId) {
        if (pendingComments.has(comment.parentCommentId)) {
          pendingComments.get(comment.parentCommentId!)!.push(comment);
        } else {
          pendingComments.set(comment.parentCommentId!, [comment]);
        }
      }

      return;
    }

    if (existingNode) {
      // Prepend or append existing node
      const index = parentNode.children.indexOf(existingNode);
      if (index > -1) {
        parentNode.children.splice(index, 1);
        if (options?.prepend) {
          parentNode.children.unshift(existingNode);
        } else {
          parentNode.children.push(existingNode);
        }
      }
    } else {
      // Attach new node
      const newNodes = attachNode(comment, parentNode, pendingComments, options);

      // Update node map with new nodes
      newNodes.forEach(n => nodeMap.set(n.id, n));
    }
  });

  // Flag all nodes in the pinned thread as pinned and move the thread's root
  // node to the top
  if (options?.pinnedId) {
    let currNode = nodeMap.get(options.pinnedId);

    // Walk up the tree to the root node
    while (currNode && currNode.id) {
      currNode.isPinned = true;

      const parentNode = nodeMap.get(currNode.parentId);

      // Move thread's root node to the top
      if (parentNode && !parentNode.id) {
        parentNode.children = [currNode, ...parentNode.children.filter(n => n.id !== currNode!.id)];
      }

      currNode = parentNode;
    }
  }

  return result;
}

function attachNode<TComment extends CommentShape>(
  comment: TComment,
  parentNode: CommentThreadNode<TComment>,
  pendingComments: Map<string, TComment[]>,
  options?: { prepend?: boolean }
): Array<CommentThreadNode<TComment>> {
  const node = {
    id: comment.id,
    children: [],
    data: { ...comment },
    isPinned: false,
    parentId: comment.parentCommentId
  };

  if (options?.prepend) {
    parentNode.children.unshift(node);
  } else {
    parentNode.children.push(node);
  }

  const result: Array<CommentThreadNode<TComment>> = [node];

  // Recursively attach pending nodes that are descendants of current node
  const childComments = pendingComments.get(comment.id);
  if (childComments) {
    childComments.forEach(childComment => {
      // Attach descendants
      const descendantNodes = attachNode(childComment, node, pendingComments, options);

      result.push(...descendantNodes);
    });

    // Remove pending comments that have been attached
    pendingComments.delete(comment.id);
  }

  return result;
}

export function buildNodeMap<TComment>(
  node: CommentThreadNode<TComment>,
  nodeMap: CommentThreadNodeMap<TComment> = new Map()
): CommentThreadNodeMap<TComment> {
  nodeMap.set(node.id, node);
  node.children.forEach(n => buildNodeMap(n, nodeMap));
  return nodeMap;
}
